import { Component } from '@theme/component';

/**
 * Store locator component.
 *
 * Renders a Leaflet map from the server-rendered store list and keeps the map
 * and list in sync: clicking a pin highlights its list entry (and vice versa),
 * and the search box + state filter narrow both the list and the visible pins.
 *
 * Leaflet is loaded as a global (`window.L`) from the vendored asset. If it is
 * unavailable the server-rendered list still works on its own.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [map]
 * @property {HTMLInputElement} [search]
 * @property {HTMLSelectElement} [state]
 * @property {HTMLElement} [count]
 * @property {HTMLElement} [list]
 * @property {HTMLElement} [noResults]
 * @property {HTMLElement[]} [item]
 *
 * @extends {Component<Refs>}
 */
class StoreLocator extends Component {
  #map;
  /** @type {Map<HTMLElement, any>} */
  #markers = new Map();

  connectedCallback() {
    super.connectedCallback();
    if (!this.refs.map) return; // empty state, nothing to wire up.
    this.#whenLeafletReady(() => this.#init());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#map?.remove();
  }

  /**
   * Leaflet ships as a deferred classic script that runs before this module,
   * but guard against timing edge cases by polling briefly for `window.L`.
   *
   * @param {() => void} callback
   */
  #whenLeafletReady(callback, attempts = 0) {
    if (window.L) return callback();
    if (attempts > 40) return; // ~2s; give up and keep the list-only fallback.
    setTimeout(() => this.#whenLeafletReady(callback, attempts + 1), 50);
  }

  #init() {
    const L = window.L;
    const el = this.refs.map;
    const zoom = parseInt(this.dataset.defaultZoom, 10) || 5;

    const icon = L.icon({
      iconUrl: this.dataset.markerIcon,
      iconRetinaUrl: this.dataset.markerIcon2x,
      shadowUrl: this.dataset.markerShadow,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });

    this.#map = L.map(el, { scrollWheelZoom: false });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(this.#map);

    for (const item of this.items) {
      const lat = parseFloat(item.dataset.lat);
      const lon = parseFloat(item.dataset.lon);
      if (Number.isNaN(lat) || Number.isNaN(lon)) continue;

      const marker = L.marker([lat, lon], { icon }).addTo(this.#map);
      marker.bindPopup(this.#popupHtml(item));
      marker.on('click', () => this.#select(item, { pan: false }));
      this.#markers.set(item, marker);
    }

    for (const item of this.items) {
      const button = item.querySelector('.store-locator__item-name');
      button?.addEventListener('click', () => this.#select(item, { pan: true }));
    }

    this.refs.search?.addEventListener('input', () => this.#applyFilter());
    this.refs.state?.addEventListener('change', () => this.#applyFilter());

    this.#fitTo(this.items, zoom);
    this.#updateCount(this.items.length);

    // Leaflet needs a size recalculation once the container has laid out.
    requestAnimationFrame(() => this.#map.invalidateSize());
  }

  get items() {
    return this.refs.item || [];
  }

  /**
   * @param {HTMLElement} item
   */
  #popupHtml(item) {
    const name = item.querySelector('.store-locator__item-name')?.textContent.trim() ?? '';
    const details = item.querySelector('.store-locator__item-details')?.innerHTML ?? '';
    const escaped = name.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    return `<div class="store-locator__popup"><h4>${escaped}</h4>${details}</div>`;
  }

  /**
   * Highlight a store in the list and focus it on the map.
   *
   * @param {HTMLElement} item
   * @param {{ pan: boolean }} options - pan/zoom the map when the list is the source.
   */
  #select(item, { pan }) {
    for (const other of this.items) other.classList.toggle('is-active', other === item);

    item.scrollIntoView({ block: 'nearest' });

    const marker = this.#markers.get(item);
    if (!marker) return;

    if (pan) {
      const zoom = Math.max(this.#map.getZoom(), 12);
      this.#map.setView(marker.getLatLng(), zoom);
    }
    marker.openPopup();
  }

  #applyFilter() {
    const query = (this.refs.search?.value || '').trim().toLowerCase();
    const state = this.refs.state?.value || '';
    const visible = [];

    for (const item of this.items) {
      const matchesText =
        !query ||
        (item.dataset.name || '').includes(query) ||
        (item.dataset.city || '').includes(query);
      const matchesState = !state || item.dataset.province === state;
      const show = matchesText && matchesState;

      item.hidden = !show;
      const marker = this.#markers.get(item);
      if (marker) {
        if (show) marker.addTo(this.#map);
        else this.#map.removeLayer(marker);
      }
      if (show) visible.push(item);
    }

    // Hide state groups that have no visible stores.
    for (const group of this.refs.list?.querySelectorAll('.store-locator__group') || []) {
      const hasVisible = group.querySelector('.store-locator__item:not([hidden])');
      group.hidden = !hasVisible;
    }

    if (this.refs.noResults) this.refs.noResults.hidden = visible.length !== 0;
    this.#updateCount(visible.length);
    this.#fitTo(visible);
  }

  /**
   * @param {HTMLElement[]} items
   * @param {number} [fallbackZoom]
   */
  #fitTo(items, fallbackZoom) {
    const points = items
      .map((item) => this.#markers.get(item)?.getLatLng())
      .filter(Boolean);

    if (points.length === 0) return;
    if (points.length === 1) {
      this.#map.setView(points[0], fallbackZoom || Math.max(this.#map.getZoom(), 10));
      return;
    }
    this.#map.fitBounds(window.L.latLngBounds(points), { padding: [30, 30] });
  }

  /**
   * @param {number} n
   */
  #updateCount(n) {
    if (this.refs.count) this.refs.count.textContent = `${n} store${n === 1 ? '' : 's'}`;
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}
